import dayjs from "dayjs";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { getAccessToken } from "../../api/getAccessToken";
import { buildAcquisitionAnalyticsParams } from "../acquisition/acquisitionSourceFilter";
import { coerceUserAnalyticsNumber } from "./userAnalyticsCoerce";
import type {
  UserAnalyticsMonthlyHttpBody,
  UserAnalyticsMonthlyPoint,
  UserAnalyticsMonthlyQueryParams,
  UserAnalyticsMonthlyResult,
} from "./userAnalyticsMonthlyTypes";

const DATE_FORMAT = "YYYY-MM-DD";

const normalizeMonth = (month: unknown): string | null => {
  if (typeof month !== "string" || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
    return null;
  }
  return `${month}-01`;
};

const buildMonthRange = (start: string, end: string): string[] => {
  const startMonth = dayjs(start).startOf("month");
  const endMonth = dayjs(end).startOf("month");
  if (!startMonth.isValid() || !endMonth.isValid()) return [];
  if (endMonth.isBefore(startMonth, "month")) return [];

  const months: string[] = [];
  let cursor = startMonth;
  while (!cursor.isAfter(endMonth, "month")) {
    months.push(cursor.format(DATE_FORMAT));
    cursor = cursor.add(1, "month");
  }
  return months;
};

export const parseUserAnalyticsMonthlyResponse = (
  body: unknown,
  params: UserAnalyticsMonthlyQueryParams,
): UserAnalyticsMonthlyResult => {
  const payload = body as UserAnalyticsMonthlyHttpBody | null | undefined;
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  const byMonth = new Map<string, UserAnalyticsMonthlyPoint>();

  rows.forEach((row) => {
    const date = normalizeMonth(row?.month);
    if (!date) return;
    byMonth.set(date, {
      date,
      signups: coerceUserAnalyticsNumber(row.signups),
      ftds: coerceUserAnalyticsNumber(row.ftds),
    });
  });

  const denseMonths = buildMonthRange(params.start_date, params.end_date);
  const points =
    denseMonths.length > 0
      ? denseMonths.map(
          (date) =>
            byMonth.get(date) ?? {
              date,
              signups: 0,
              ftds: 0,
            },
        )
      : Array.from(byMonth.values()).sort((a, b) =>
          a.date.localeCompare(b.date),
        );

  return {
    points,
    filters: Array.isArray(payload?.meta?.filters)
      ? payload.meta.filters
      : [],
  };
};

export const fetchUserAnalyticsMonthly = async (
  params: UserAnalyticsMonthlyQueryParams,
): Promise<UserAnalyticsMonthlyResult> => {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_USER_ANALYTICS_MONTHLY, {
    params: buildAcquisitionAnalyticsParams(params),
  });
  return parseUserAnalyticsMonthlyResponse(response?.data, params);
};
